import React, { useEffect, useState } from 'react';
import { ActivityIndicator, View, Image, FlatList } from "react-native";
import { Cloudinary, CloudinaryImage, CloudinaryVideo } from "@cloudinary/base";
import { AdvancedImage, AdvancedVideo, lazyload, placeholder } from '@cloudinary/react';
import { fill, fillPad, fit } from "@cloudinary/base/actions/resize";
import { autoGravity } from "@cloudinary/base/qualifiers/gravity";
import { Text, TouchableOpacity } from '../../../globalComponents/NativeOverrides';
import Colors from '../../../constants/Colorsv2';
import { Icon } from 'react-native-elements';
import VideoPlayer from './VideoPlayer';
import { CloudinaryImageResponse } from '../../../objectTypes';
import { useDispatch } from 'react-redux';
import { V1_LOAD_FULL_SIZE_IMAGE } from '../../../store/actionsv1/types';
import StreamCard from './StreamCard';

const cld = new Cloudinary({
    cloud: {
      cloudName: 'hoabts6mc'
    },
    url: {
      secure:true
    }
  });

type ImageEntityProps = {
    height:number,
    width:number,
    additional_num?:string,
    image: any
}


const ImageEntity = ({ image, height, width, additional_num }:ImageEntityProps) => {
    const [ loading, setLoading ] = useState(false);
    const [ cld_image, setCLDImage ] = useState<CloudinaryImage | undefined>(undefined)
    
    const dispatch = useDispatch();

    useEffect(() => {
        if(loading){ return }
        loadImage()
    },[height, width])

    const loadImage = async() => {
        setLoading(true)
        let new_image = await cld.image(image.public_id)
        .resize(fit().width(width).height(height))
        setCLDImage(new_image)
        setLoading(false)
    }

    if(!cld_image){ return (<></>)}
      return (
          <TouchableOpacity onPress={() => dispatch({ type: V1_LOAD_FULL_SIZE_IMAGE, full_size_image: image })}>
              {loading ?
              <ActivityIndicator size='small' color={Colors.brand.midnight} />
              :
                <AdvancedImage cldImg={cld_image}/>
              }
              {additional_num ?
              <View style={{ position:'absolute', justifyContent:'center', alignItems:'center', top:0, right:0, left:0, bottom:0, backgroundColor:Colors.highlights.highlight200Faded }}>
                  <Text size={20} color={Colors.shades.white} weight='bold'>{additional_num}</Text>
              </View>
              :<></>}
          </TouchableOpacity>
      )
  }


  type VideoEntityProps = {
    height:number,
    width:number,
    additional_num?:string,
    video: any
}
  const VideoEntity = ({ height, width, video }:VideoEntityProps) => {
        const [ cld_video, setCLDVideo ] = useState<CloudinaryVideo | undefined>()

        useEffect(() => {
            loadVideo()
        },[height, width])

        const loadVideo = async() => {
            let vid_ratio = video.height / video.width
            let new_video = cld.video(video.public_id)
            .resize(fit().width(width))
            setCLDVideo(new_video)
        }

        if(!cld_video){ return <></> }
        return (
            <TouchableOpacity>
                <AdvancedVideo playsInline cldVid={cld_video} plugins={[lazyload(), placeholder()]} controls/>
            </TouchableOpacity>
        )
  }



type MediaHolderProps = {
    images: any[],
    videos: any[],
    width: number,
    view_mode: 'view' | 'edit'
    onRemoveMedia?: (media:any, media_type:'image'|'video') => void
}

const MAX_WIDTH = 300

const MediaHolder = ({ images, videos, view_mode, width, onRemoveMedia }:MediaHolderProps) => {
    const handleRemoveMedia = (media:any, media_type:'image'|'video') => {
        if(!onRemoveMedia){ return }
        onRemoveMedia(media, media_type)
    }

    const scaleGiphySize = (giphy:any, giphy_width:number) => {
        if(!giphy?.width || !giphy?.height){ return { width: giphy_width, height: giphy_width * 0.75 } }
        let wtohratio = giphy.width / giphy.height
        return { width: giphy_width, height: giphy_width * wtohratio }
    }

    let image_width = width
    if(width > MAX_WIDTH){ image_width = MAX_WIDTH }

    const renderImage = (image:any) => {
        switch(image.type){
            case 'giphy':
                let giphy_width = scaleGiphySize(image, image_width)
                return <Image source={image.url} style={{ height:giphy_width.height, width:giphy_width.width }} resizeMode={'cover'}/>
            case 'upload': return <ImageEntity width={image_width} height={image_width * 0.5} image={image}/>
            default: return <></>

        }
    }

    const renderVideo = (video:any) => {
        switch(video.video_source){
            case 'youtube': 
                return <StreamCard id={video.embed_id} stream_type='youtube' width={width} url={video.url}/>
            case 'local':
                return <VideoEntity width={image_width} height={image_width * 0.5} video={video} />
            default: 
                return <></>
        }
    }


    const renderMedia = (data: {item:any, index:number}) => {
        return (
            <View style={{ margin:5 }}>
                {data.item.resource_type === 'image' ?
                    renderImage(data.item)
                : data.item.resource_type === 'video' ?
                    renderVideo(data.item)
                :<></>}
                {view_mode === 'edit' ?
                    <TouchableOpacity style={{ position:'absolute', justifyContent:'center', alignItems:'center', borderRadius:100, top:5, right:5, width:20, height:20, backgroundColor:Colors.shades.white }} onPress={() => handleRemoveMedia(data.item, data.item.resource_type)}>
                        <Icon name='x' type='feather' color={Colors.brand.slate} size={16}/>
                    </TouchableOpacity>
                :<></>}
            </View>
        )
    }

    

    let media_sizes = []//getMediaSize()
    let medias:any[] = images.concat(videos)
    
    return (
        <View style={{ flex:1, justifyContent:'center', alignItems:'center' }}>
            <FlatList listKey='media' style={{ flex:1 }} data={medias} renderItem={renderMedia} horizontal keyExtractor={(item, index) => index.toString()}/>
        </View>
    )

}

const areEqual = (prevProps:MediaHolderProps, nextProps:MediaHolderProps) => {
    if(prevProps.images.length !== nextProps.images.length){ return false }
    if(prevProps.videos.length !== nextProps.videos.length){ return false }
    if(prevProps.width !== nextProps.width){ return false }
    return true
}

export default React.memo(MediaHolder, areEqual)




